/**
 * template.js — THE case report template. One source of truth.
 *
 * Deepak, 2026-09-26: "one source of truth… revisit the templates and create what is right based
 * on what all information you have gathered… share back the GIMP files so that the manual versions
 * can be created by team and you do the automated ones based on the new template we propose."
 *
 * Everything below is data. The Builder renders from it. The GIMP files the team draws on are
 * GENERATED from it (tools/gimp-template/), never drawn by hand, so a GIMP page and a Builder
 * page cannot disagree. When this file changes, the GIMP files are regenerated
 * (tools/gimp-template/run.sh, into its own out/ folder) and copied to the team's folder by hand,
 * on Deepak's instruction; the delivered set's date and MD5s are recorded in the project memory,
 * not here. Nothing checks the delivered files against this file — a regeneration that was not
 * copied leaves the team on the previous set.
 *
 * ── Where every number comes from ─────────────────────────────────────────────────────────────
 * THE MASTER: the team's reference page of 26 Sep 2026,
 *   /Users/deepakrajk/Documents/Temp/GimpTemplates/Referene Page Template for Case Report.xcf
 * measured by tools/xcf-geometry/extract.py and recorded, with the file's md5, in
 * tools/gimp-template/master-reference.json — template.test.js asserts CHASSIS against it.
 * The two delivered reports (ORTHO 17 pp, FIBULA 23 pp) still decide the ten page TYPES below.
 *
 * Decisions applied on top of what was measured, each named where it lands:
 *   DK-1     26 Sep: no product line, and NO version is printed anywhere — "it is going to be
 *            generated by portal, so you can keep something hidden and small just for you to
 *            read". It lives in the PDF's document properties and the XCF's comment.
 *   MASTER   26 Sep: "take the watermark and all other stuff exactly from this" — the master
 *            replaces the earlier chassis fitted from the delivered reports (TEAM-1, TEAM-2 and
 *            DK-2 are all satisfied by the master itself).
 *
 * ── Shape ──────────────────────────────────────────────────────────────────────────────────────
 * `CHASSIS` is exactly the geometry object layout.js consumes (see formats.js geometryFor), so it
 * can be handed to layoutReport() as `geometry` with no adapter. Millimetres; text positions are
 * BASELINES. Header and footer rules are recorded as TOP EDGES (`rule()` converts the master's
 * centres); the content frame's rules (`frames`, `content`, `contentRows`, `contentCols`) are
 * CENTRES and layout.js draws its paths on them.
 */

// ── the chassis ──────────────────────────────────────────────────────────────────────────────

/** A4. Every delivered page. */
const PAGE = { w: 210, h: 297 }

/**
 * Side margin for text and the header/footer rules: the master frame's left EDGE (colRules[0]
 * 7.75 less half its 1 px rule). The frame itself is drawn between `contentCols` (7.75 → 202.73).
 */
const MARGIN = 7.7

/**
 * Header and footer rules are stated in layout.js as TOP EDGES and drawn `width` thick; the
 * extractor measures a rule at its CENTRE. One conversion, here, for every header and footer
 * rule of the master. The content frame's rules are recorded as centres and need none.
 */
const HAIR = 0.254                                   // MASTER header/footer rules, 3 px at 300 dpi
const rule = (centre, width = HAIR) => ({ y: Math.round((centre - width / 2) * 100) / 100, width })

/**
 * THE MASTER — `Referene Page Template for Case Report.xcf`, the team's page of 26 Sep 2026,
 * measured by tools/xcf-geometry/extract.py (the same instrument that read the twelve). Every
 * number below is that measurement, cited `MASTER`. Text positions are BASELINES; the extractor
 * derives a baseline from a text layer's box top and its point size (MULISH_ASCENT_RATIO).
 *
 * Four departures, each stated where it lands (plan of 26 Sep, approved; PRINTS 29 Sep):
 *   CENTRED   the two title lines are centred on the page; the master's layers sit 1.2 / 3.1 mm
 *             left of centre, by hand. A nudge is not a spec.
 *   NUMBERED  the page number is printed; the master hides its layer because the team cannot
 *             know the number when drawing a page. The portal does.
 *   FIT       a caption that would overrun the frame shrinks, to a floor of 60 %; the master
 *             cannot shrink, a page can.
 *   PRINTS    the frame is stroked at the header/footer rules' weight, not the master's 1 px
 *             raster line — see `gridRule` below for the measurements.
 */
export const CHASSIS = {
  id: 'house',
  templateMark: `osteo3d-case-report-template=T2.1`,   // = TEMPLATE_MARK; stamped into PDF properties, never drawn
  page: PAGE,
  margin: MARGIN,
  contentW: 195,                                   // 7.7 → 202.7, MASTER colRules[2] 202.73

  // MASTER layer `Osteo3d® 3d printing healthcare® #4`: x 76.62, y 1.69, w 57.49, h 16.51 —
  // the layer's own pixels (679 × 195 px), shipped as public/case-report-logo.png and drawn in
  // that exact box. Not the app's general logo PNG, whose crop is different.
  logo: { x: 76.62, top: 1.69, width: 57.49, height: 16.51, asset: '/case-report-logo.png' },

  // MASTER `Online Surgical Planning #1` / `Case Report of …`: Mulish Regular 14.4 pt, baselines
  // 24.908 / 31.428, justified centre. CENTRED on the page. DK-1: nothing else on these lines.
  // `oneLineBaseline` — where a template that states ONE header line puts it.
  // MEASURED, not derived: the delivered `Images for Design Confirmation` document
  // sets its single line with ink 22.73 → 27.68 (the descender of "Design"), on a
  // baseline of 26.60 (geometry.js, the design-confirmation geometry). A midpoint
  // of the master's two baselines would be 28.17, which is 1.57 mm lower than the
  // delivered page. One line sits HIGH in the block, not centred in it.
  title: { line1Baseline: 24.91, line2Baseline: 31.43, oneLineBaseline: 26.60, size: 14.4 },

  ruleUnderTitle: rule(34.33),                     // MASTER `Header Lines` row 34.33, 0.254 thick

  // MASTER identity block: one column, Mulish Regular 12 pt, every label at x 7.705, baselines
  // 38.862 / 44.281 / 49.445 / 54.102 — taken as they are. The label carries its own colon
  // (`Surgeon name: `) and the value follows it on the line: `colonAttached`.
  info: {
    size: 12,
    lineH: 5.42,                                   // 1.28 × 12 pt, the measured Mulish ratio
    columns: 1,
    colonAttached: true,
    rows: [
      { key: 'surgeonLabel', x: 7.705, baseline: 38.86 },
      { key: 'hospitalLabel', x: 7.705, baseline: 44.28 },
      { key: 'patientLabel', x: 7.705, baseline: 49.45 },
      { key: 'caseIdLabel', x: 7.705, baseline: 54.10 },
    ],
    // Kept for the 2×2 code path; unused when `rows` is present.
    row1Baseline: 38.86, row2Baseline: 44.28, rightColX: 7.705,
  },
  ruleUnderInfo: rule(55.33),                      // MASTER `Header Lines` row 55.33

  // MASTER `Native skull`: 14.4 pt, baseline 60.045, centred — the page heading above the frame.
  // MASTER `Preoperative images`: 14.4 pt, baseline 274.083 — the caption below it. FIT applies.
  captionTop: { baseline: 60.05, size: 14.4, fit: true, fitFloor: 0.6 },
  captionBottom: { baseline: 274.08, size: 14.4, fit: true, fitFloor: 0.6 },

  // MASTER carries TWO frame layers, and every picture page of the delivered reports sits in
  // one of them (Deepak, 26 Sep: pages 6, 8, 9, 10 were unboxed for want of the second):
  //   `Grid - Native Skull`  the 3×2 grid: cols 7.75 / 105.2 / 202.73, rows 61.17 / 130.43 /
  //                          199.69 / 268.94 — six views, comparison
  //   `Grid`                 the single-view box: cols 7.75 / 202.73, rows 61.17 / 268.94 — the
  //                          outer frame alone; single view, steps, measurements, colour map,
  //                          gallery
  // Rule centres. The frame's path IS the rule centre: a PDF stroke is centred on its path, so
  // `content` carries the master's centres unchanged. The stroke weight is `gridRule`, below.
  frames: {
    grid: { layer: 'Grid - Native Skull', cols: [7.75, 105.2, 202.73], rows: [61.17, 130.43, 199.69, 268.94] },
    single: { layer: 'Grid', cols: [7.75, 202.73], rows: [61.17, 268.94] },
  },
  content: { top: 61.17, bottom: 268.94 },
  contentRows: [61.17, 130.43, 199.69, 268.94],
  contentFrameStated: true,
  contentCols: [7.75, 105.2, 202.73],
  contentColCentres: [56.48, 153.97],
  frame: 'boxed',
  // The frame's stroke. The master's grid is a 1 px raster line (0.085 mm); a VECTOR stroke that
  // thin never covers a whole device pixel and prints grey or broken (team, 29 Sep). 0.18 mm
  // printed black at 300 dpi but still broke on the team's print path (30 Sep: lighter, broken,
  // missing in places): a page rasterised at 72–120 dpi loses a 0.18 line by pixel-grid phase
  // and keeps a 0.254 one. So the frame carries the header/footer rules' weight, which the team
  // sees print. template.test.js pins frame = rule weight.
  gridRule: { width: HAIR },
  viewBadge: { size: 17, inset: 2 },

  ruleFooter: rule(276.48),                        // MASTER `Lines` row 276.48

  footer: {
    inset: 0,
    // MASTER `Images are not to scale \t…\tAll units are in mm unless specified`: ONE layer,
    // Mulish Bold 10.8 pt, baseline 281.0; the two phrases at the measured ink edges 28.87 / 181.53.
    showNotToScale: true,
    notToScaleBaseline: 281.0,
    notToScaleSize: 10.8,
    notToScaleParts: ['Images are not to scale', 'All units are in mm unless specified'],
    notToScaleInkLeft: 28.87,
    notToScaleInkRight: 181.53,
    // MASTER `Copyright © 2026 …`: ONE three-line layer, Mulish MEDIUM 7.2 pt, black, centred at
    // 105.2; first baseline 284.615, line pitch 3.30 (ink tops 282.79 / 286.09 / 289.39).
    // Lines 1–2 as the master breaks them; line 3 is phone LEFT and email RIGHT at the measured
    // ink edges 39.03 / 171.20.
    copyrightLines: [
      "Copyright © {year} Osteo3d®. All rights reserved. 'Osteo3d®' and '3d printing healthcare®' are registered trademarks.",
      'Unauthorized use/replication is prohibited. We do not have any representatives or distributors',
    ],
    copyrightBaselines: [284.62, 287.92],
    copyrightSize: 7.2,
    copyrightWeight: 'medium',
    copyrightInk: true,
    contactBaseline: 291.22,
    contactSize: 7.2,
    contactParts: ['Phone: +91-9900265669', 'Email: contactus@Osteo3d.com'],
    contactInkLeft: 39.03,
    contactInkRight: 171.20,
    // MASTER `Page 11 of 21` (hidden layer — NUMBERED): Mulish Regular 9.6 pt, LEFT-anchored at
    // x 177.292, baseline 291.913. DK-1: no product line.
    showProductLine: false,
    showPageNumbers: true,
    pageNumberX: 177.29,
    pageNumberAlign: 'left',
    pageNumberBaseline: 291.91,
    pageNumberSize: 9.6,
    padTotal: true,                                // "Page 11 of 21", as the master's own layer
    // Retained for the code path that still reads them; not drawn when showProductLine is false.
    productBaseline: 291.91,
    productSize: 9.6,
  },

  // MASTER `Watermark - Mulish 30deg V2 #1`, measured off the layer: pitch, size, angle,
  // opacity and colour identical to V1.3.5's. The PHASE of the lattice is the master's too:
  // phase correlation of our render against the master's raster in two blank regions put our
  // tiles 13.46 mm left and 4.91 mm above the master's (tools/gimp-template/compare_master.py),
  // so the first tile is placed one pitch back from (-10 + 13.46, 16.849 + 4.91).
  watermark: {
    text: 'Osteo3d ®', size: 19.97, opacity: 0.1686, angle: 30,
    stepX: 58.081, stepY: 16.849, color: [140, 150, 155],
    originX: -54.62, originY: 4.91,
  },

  // MASTER page variant `Singe View` (hidden): the SOURCE DATA page. No render slots. Heading
  // `CT/ Source data information` 16.8 pt left at x 8.551, baseline 69.291; two 12 pt text columns
  // (`Skull data…` at x 8.89 from baseline 76.031; `Intraoral scan/ Denture scan…` at x 104.055
  // from baseline 76.2); ` Quality waiver` at x 9.229, baseline 203.877. The master also carries
  // a case-ID layer (`O3D-2026-XX-XX-00XX_x`, 12 pt at x 9.059) and a date layer (`YYYY-MMM-DD`)
  // on the caption line; the team's test of 28 Sep asked for the case ID there to go ("can be
  // removed"), so neither is printed — the case ID is in the identity block on every page.
  sourceData: {
    heading: { x: 8.55, baseline: 69.29, size: 16.8 },
    columns: [{ x: 8.89, baseline: 76.03, right: 100.16 }, { x: 104.06, baseline: 76.20, right: 201.59 }],
    size: 12,
    lineH: 5.42,
    waiver: { x: 9.23, baseline: 203.88 },
    // MASTER `YYYY-MMM-DD`: Mulish Medium 13.199 pt, RIGHT-justified in a fixed box x 166.455
    // w 36.237, so its right edge is 202.69 — the content frame's right rule. Baseline 273.65,
    // on the caption line, 2.83 mm above the footer rule. master-reference.json
    // pageText.sourceData.date; the planning-images and fibula templates carry the same layer.
    //
    // Drawn from `meta.dateStr`, the Builder's own "Report date" field, and only by
    // sourceDataOps — so only the source-data page carries it. Team, 5 Oct 2026: "Include the
    // date on which the report was generated in the images"; Deepak: "only on page 1 is the ask".
    //
    // The master's case-ID layer on the SAME line stays unprinted: the team asked for it to go
    // on 28 Sep ("Case ID in left bottom corner can be removed") and the identity block carries
    // the case ID on every page anyway.
    date: { right: 202.69, baseline: 273.65, size: 13.199 },
  },
}

/**
 * One version for the whole set. NEVER printed (DK-1). It is written into the PDF's document
 * properties (`Creator`, `Keywords`) by buildCaseReport and into the XCF comment by the GIMP
 * generator, so a delivered file can be traced to the template that made it without the page
 * carrying it. T2.1 = the master of 26 Sep 2026.
 */
export const TEMPLATE_VERSION = 'T2.1'
export const TEMPLATE_MARK = `osteo3d-case-report-template=${TEMPLATE_VERSION}`

// ── the ten page types ───────────────────────────────────────────────────────────────────────

/**
 * Every one of the 40 delivered pages is one of these ten. `delivered` cites them by report and
 * page number, and template.test.js asserts the two reports are covered completely, with no page
 * claimed twice.
 *
 * `who` is the split — not what the engine can draw, but WHO KNOWS THE NUMBER:
 *   builder   the Builder makes the page from files and typed fields
 *   annotate  the Builder lays the page out; the team places the numbers, arrows and insets
 *   image     the Builder lays the page out around an image the team supplies (a ZBrush render)
 * `gimp` names the generated GIMP file the team uses while the annotate path is still by hand.
 */
export const PAGE_TYPES = [
  {
    id: 'agreement', block: 'text', who: 'builder',
    title: 'Agreement',
    delivered: { orthognathic: [1], fibula: [1] },
  },
  {
    id: 'source-data', block: 'info', who: 'builder',
    title: 'Source data',
    // FIBULA p2: skull data + leg data as label/value lists, one render each, and a CASE SUMMARY
    // paragraph ("The left fibula has been utilized, with the first osteotomy commencing 8 cm from
    // the lateral malleolus…"). The summary is a typed field.
    fields: ['summary'],
    delivered: { orthognathic: [], fibula: [2] },
  },
  {
    id: 'six-views', block: 'grid', grid: { rows: 3, cols: 2 }, who: 'builder',
    title: 'Six views',
    delivered: { orthognathic: [2, 3, 13, 14], fibula: [3, 5, 6, 8, 15, 18] },
  },
  {
    id: 'comparison', block: 'grid', grid: { rows: 3, cols: 2, columns: ['native', 'post-plan'] }, who: 'builder',
    title: 'Comparison',
    // ORTHO p10 A/RL/LL and p11 P/S/I: native left, post-plan right, one shared scale.
    delivered: { orthognathic: [10, 11], fibula: [] },
  },
  {
    id: 'single-view', block: 'grid', grid: { rows: 1, cols: 1 }, who: 'annotate', gimp: 'annotated-page',
    title: 'Single view with callouts',
    // ORTHO p4: one render, a magnifier inset, a colour highlight and its caption.
    // FIBULA p4 legs with arteries, p7 resection margins with dimensions, p16 / p17 the mandible.
    annotations: ['circle', 'leader', 'text', 'inset', 'dimension'],
    delivered: { orthognathic: [4], fibula: [4, 7, 16, 17] },
  },
  {
    id: 'steps', block: 'steps', grid: { rows: 2, cols: 2 }, who: 'annotate', gimp: 'annotated-page',
    title: 'Step sequence',
    // ORTHO p5: four panels — "Step 01: Lefort I osteotomy…", "Step 02: The maxilla was advanced
    // by 6 mm" with a dimension arrow, "Step 03: rotated by 3.43° counterclockwise…", "Step 04…".
    annotations: ['arrow', 'dimension', 'angleArc', 'text'],
    delivered: { orthognathic: [5], fibula: [] },
  },
  {
    id: 'dimensioned', block: 'measure', who: 'annotate', gimp: 'annotated-page',
    title: 'Dimensioned views',
    // ORTHO p8 / p9: native and post-plan skull, A over RL+LL, vertical dimensions with a Notes
    // block ("The measurements are taken from the infraorbital rim to the incisor tip…").
    // FIBULA p14: the fibula with eleven straight-line dimensions and a note.
    annotations: ['dimension', 'text'],
    fields: ['notes'],
    delivered: { orthognathic: [8, 9], fibula: [14] },
  },
  {
    id: 'colour-map', block: 'gallery', who: 'image', gimp: 'colour-map',
    title: 'Colour map',
    // ORTHO p6 / p7: teeth-interference thickness analysis, magnifier insets, legend 0.10–1.50.
    // FIBULA p19: reconstructed mandible thickness, legend 2 / 7.25 / 12.5 mm. The render is a
    // ZBrush image; the legend is drawn from data.
    fields: ['image', 'legend'],
    delivered: { orthognathic: [6, 7, 12], fibula: [19] },
  },
  {
    id: 'gallery', block: 'gallery', who: 'builder',
    title: 'Gallery',
    // N renders on one page, each with a view badge or a label.
    // Views of one object, not the standard six: not "six views" and not "single
    // view", they are a row of chosen views. The coverage test found them uncovered
    // on 2026-09-26.
    //
    // 🔴 Counted from the reference dump on 1 Oct 2026, because the counts written
    // here were wrong. The badge LAYERS are not the views: on the leg pages BOTH
    // laterals are present with one hidden, beside a "Left leg" / "Right leg"
    // caption with one hidden. Measured, by visible badge:
    //   Page 4  Native Legs      4 — A, LL, P, RL   (all visible, four columns)
    //   Page 10 Leg and Fibula   3 — A, LL, P       (RL hidden: the right-leg case)
    //   Page 11 Leg with Guide   3 — A, LL, P       (RL hidden)
    //   Page 12 Fibula and Guide 1 — no view badge at all, Proximal/Distal labels
    //   Page 13 Only Fibula Guide 1 — one hidden P badge, Proximal/Distal labels
    // All five are built by `g-view-row`. Pages 12 and 13 print four views, A, LL, P, RL,
    // where the template shows one: the team asked for four on 19 Oct 2026.
    // ORTHO p15, FIBULA p20 / p21: print models with leader-line labels ("Trial fitment
    // model – Anterior mandible", "Fibula osteotomy guide (open type…)").
    annotations: ['leader'],
    delivered: { orthognathic: [15], fibula: [9, 10, 11, 12, 13, 20, 21] },
  },
  {
    id: 'terms', block: 'text', who: 'builder',
    title: 'Terms and conditions',
    delivered: { orthognathic: [16, 17], fibula: [22, 23] },
  },
]

/** The two GIMP files generated for the team. Each is the chassis plus the slots of its page types. */
export const GIMP_FILES = {
  'annotated-page': { pageTypes: ['single-view', 'steps', 'dimensioned'] },
  'colour-map': { pageTypes: ['colour-map'] },
}

/** Delivered page counts, so coverage can be asserted rather than assumed. */
export const DELIVERED_PAGES = { orthognathic: 17, fibula: 23 }

export const pageTypeFor = (id) => PAGE_TYPES.find((t) => t.id === id)
